import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Check, X, AlertCircle } from 'lucide-react';

// Blocked free email domains - must match backend list
const BLOCKED_FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'yahoo.com', 'yahoo.co.uk', 'yahoo.ca', 'ymail.com', 'rocketmail.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'aim.com',
  'mail.com', 'protonmail.com', 'proton.me', 'zoho.com',
  'gmx.com', 'gmx.net', 'yandex.com', 'tutanota.com', 'fastmail.com'
]);

const isBlockedEmailDomain = (email) => {
  if (!email || !email.includes('@')) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return BLOCKED_FREE_EMAIL_DOMAINS.has(domain);
};

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    organization_name: '',
    password: '',
    confirm_password: ''
  });

  // Email validation state
  const emailHasBlockedDomain = isBlockedEmailDomain(formData.email);

  const passwordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    return strength;
  };

  const getStrengthLabel = (strength) => {
    if (strength <= 2) return { label: 'Weak', color: 'bg-red-500' };
    if (strength <= 3) return { label: 'Medium', color: 'bg-yellow-500' };
    return { label: 'Strong', color: 'bg-green-500' };
  };

  const strength = passwordStrength(formData.password);
  const strengthInfo = getStrengthLabel(strength);

  const passwordChecks = {
    length: formData.password.length >= 8,
    lowercase: /[a-z]/.test(formData.password),
    uppercase: /[A-Z]/.test(formData.password),
    number: /[0-9]/.test(formData.password)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for blocked email domains
    if (emailHasBlockedDomain) {
      toast.error('Please use your work email. Free email providers (Gmail, Yahoo, Outlook, etc.) are not allowed.');
      return;
    }
    
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (!passwordChecks.length || !passwordChecks.lowercase || !passwordChecks.uppercase || !passwordChecks.number) {
      toast.error('Password does not meet requirements');
      return;
    }

    setLoading(true);

    try {
      await signup(formData);
      toast.success('Account created! Please check your email to verify your account.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const CheckItem = ({ checked, label }) => (
    <div className="flex items-center gap-2 text-sm">
      {checked ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <X className="w-4 h-4 text-red-500" />
      )}
      <span className={checked ? 'text-green-700' : 'text-[#5A5A5A]'}>{label}</span>
    </div>
  );

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-[#F8F9FA] px-4 py-8"
      data-testid="signup-page"
    >
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="space-y-4 pb-2">
          {/* Centered Logo */}
          <div className="flex justify-center">
            <img 
              src="/ors-logo.svg" 
              alt="Onsite Rail Solutions" 
              className="h-12 w-auto"
              srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
              data-testid="signup-logo"
            />
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#323033]">Create your account</h1>
            <p className="text-sm text-[#5A5A5A] mt-1">Start your 14-day free trial</p>
          </div>
        </CardHeader>

        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  maxLength={50}
                  data-testid="signup-first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  placeholder="Smith"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  maxLength={50}
                  data-testid="signup-last-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Organization Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@yourcompany.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className={emailHasBlockedDomain ? 'border-red-500 focus:ring-red-500' : ''}
                data-testid="signup-email-input"
              />
              {emailHasBlockedDomain ? (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4" />
                  <span>Please use your work email. Free email providers are not allowed.</span>
                </div>
              ) : (
                <p className="text-xs text-[#808080]">Use your organizational/work email address</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization_name">Organization Name</Label>
              <Input
                id="organization_name"
                placeholder="Your Company Inc."
                value={formData.organization_name}
                onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                required
                maxLength={100}
                data-testid="signup-org-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#808080] hover:text-[#323033]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {formData.password && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${strengthInfo.color}`}
                        style={{ width: `${(strength / 5) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-[#5A5A5A]">{strengthInfo.label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <CheckItem checked={passwordChecks.length} label="8+ characters" />
                    <CheckItem checked={passwordChecks.lowercase} label="Lowercase" />
                    <CheckItem checked={passwordChecks.uppercase} label="Uppercase" />
                    <CheckItem checked={passwordChecks.number} label="Number" />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <Input
                id="confirm_password"
                type="password"
                placeholder="Confirm your password"
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                required
                data-testid="signup-confirm-password-input"
              />
              {formData.confirm_password && formData.password !== formData.confirm_password && (
                <p className="text-sm text-red-500">Passwords do not match</p>
              )}
            </div>

            {/* Terms & Conditions Disclosure */}
            <div 
              className="p-3 bg-[#005EF8]/5 border border-[#005EF8]/20 rounded-lg"
              data-testid="terms-disclosure"
            >
              <p className="text-xs leading-relaxed text-[#323033] text-center">
                By creating an account, you agree to the Onsite Rail Solutions{' '}
                <a
                  href="https://www.onsiterailsolutions.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#005EF8] font-medium underline hover:text-[#0047C2] visited:text-[#003896] transition-colors"
                  data-testid="terms-link"
                >
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a
                  href="https://www.onsiterailsolutions.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#005EF8] font-medium underline hover:text-[#0047C2] visited:text-[#003896] transition-colors"
                  data-testid="privacy-link"
                >
                  Privacy Policy
                </a>.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#005EF8] hover:bg-[#0047C2] text-white h-11"
              disabled={loading}
              data-testid="signup-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-[#5A5A5A]">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-[#005EF8] hover:text-[#0047C2] font-medium"
              data-testid="login-link"
            >
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignupPage;
